/**
 * Wire schemas for the referral flow endpoints (PR #5d).
 *
 * Referrals allow a broker or junior underwriter to submit a priced quote
 * for senior-underwriter approval when they lack bind authority.
 */

import { z } from 'zod';

import { AppliedFacilitySchema, PolicyContextSchema } from './quote.js';

export const ReferralStatusSchema = z.enum(['open', 'approved', 'declined', 'expired']);

/**
 * A referral record as returned by the API.
 *
 * `grossPremium` is a number (the API converts the numeric DB string).
 * Timestamps are ISO-8601 strings.
 */
export const QuoteReferralSchema = z.object({
  id: z.string().uuid(),
  quoteRef: z.string().min(1),
  grossPremium: z.number().positive(),
  facilityId: z.string().uuid().nullable(),
  facilityVersion: z.number().int().positive().nullable(),
  status: ReferralStatusSchema,
  requestedByUserId: z.string().uuid(),
  requestedByEntityId: z.string().uuid(),
  reviewedByUserId: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  reviewerNotes: z.string().nullable(),
  createdAt: z.string().datetime(),
});

/**
 * Body sent by the SPA to `POST /quotes/refer`.
 * Mirrors `QuoteBindRequestSchema` — the server re-runs the engine on
 * this payload to get an authoritative grossPremium before storing it.
 */
export const QuoteReferRequestSchema = z.object({
  context: PolicyContextSchema,
  appliedFacility: AppliedFacilitySchema.nullable(),
});

export const QuoteReferResponseSchema = z.object({
  referral: QuoteReferralSchema,
});

/** Body for `GET /quotes/referrals` list response. */
export const QuoteReferralListResponseSchema = z.object({
  referrals: z.array(QuoteReferralSchema),
});

/** Body for `POST /quotes/referrals/:id/approve` — optional reviewer notes. */
export const QuoteReferralApproveRequestSchema = z.object({
  notes: z.string().max(2000).optional(),
});

/** Body for `POST /quotes/referrals/:id/decline` — notes required. */
export const QuoteReferralDeclineRequestSchema = z.object({
  notes: z.string().min(1, 'Decline reason is required').max(2000),
});

/** Response for approve / decline actions. */
export const QuoteReferralActionResponseSchema = z.object({
  referral: QuoteReferralSchema,
});

export type ReferralStatus = z.infer<typeof ReferralStatusSchema>;
export type QuoteReferral = z.infer<typeof QuoteReferralSchema>;
export type QuoteReferRequest = z.infer<typeof QuoteReferRequestSchema>;
export type QuoteReferResponse = z.infer<typeof QuoteReferResponseSchema>;
export type QuoteReferralListResponse = z.infer<typeof QuoteReferralListResponseSchema>;
export type QuoteReferralApproveRequest = z.infer<typeof QuoteReferralApproveRequestSchema>;
export type QuoteReferralDeclineRequest = z.infer<typeof QuoteReferralDeclineRequestSchema>;
export type QuoteReferralActionResponse = z.infer<typeof QuoteReferralActionResponseSchema>;
